// Overview — admin home. Inbox at top (the highest-leverage panel),
// staff identity card below for confirmation, and a "What ships next"
// list that points to the remaining sub-phases.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, BellOff, ExternalLink, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient, useStaffMe } from '../lib/providers.js';
import {
  INBOX_QUERY_KEY,
  platformApi,
  severityBadgeVariant,
  type InboxItem,
} from '../lib/api/platform.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import { Label } from '../components/ui/label.js';
import { Select } from '../components/ui/select.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { ErrorRetry } from '../components/ui/error-retry.js';
import { useState } from 'react';

const SNOOZE_HOURS = [
  { value: 1, label: '1 hour' },
  { value: 4, label: '4 hours' },
  { value: 24, label: '1 day' },
  { value: 168, label: '1 week' },
] as const;

export function OverviewPage() {
  const me = useStaffMe();
  const client = useApiClient();
  const inboxQ = useQuery({
    queryKey: INBOX_QUERY_KEY,
    queryFn: () => platformApi.listInbox(client),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">
          Welcome, {me.email}. The Attention Inbox below is your starting
          point — anything in there needs human attention today.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Inbox aria-hidden className="h-4 w-4" />
            Attention Inbox
          </CardTitle>
          {inboxQ.data && inboxQ.data.items.length > 0 && (
            <Badge variant="destructive">
              {inboxQ.data.items.filter((i) => i.severity === 'critical').length} critical
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {inboxQ.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : inboxQ.isError ? (
            <ErrorRetry
              title="Failed to load Inbox"
              error={inboxQ.error}
              onRetry={() => {
                void inboxQ.refetch();
              }}
            />
          ) : inboxQ.data.items.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center">
              <p className="text-sm font-medium">All clear ✨</p>
              <p className="mt-1 text-xs text-muted-foreground">
                No webhook errors, drift alerts, ticket SLA breaches, or
                security spikes need human attention right now.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {inboxQ.data.items.map((item) => (
                <InboxRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your staff identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Staff id" value={<code className="text-xs">{me.staffId}</code>} />
            <Row label="Email" value={me.email} />
            <Row
              label="Scopes"
              value={
                <div className="flex flex-wrap gap-1.5">
                  {me.scopes.map((s) => (
                    <Badge key={s} variant="secondary">
                      {s}
                    </Badge>
                  ))}
                </div>
              }
            />
            <Row
              label="MFA"
              value={
                me.mfaVerified ? (
                  <Badge variant="default">verified</Badge>
                ) : (
                  <Badge variant="destructive">missing</Badge>
                )
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">What ships next</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
              <li>P7 — Hardening (a11y, e2e, security tests, CSP, doc updates).</li>
              <li>
                Follow-ups: SSRF audit emit, Inbox snooze polish, Slack daily digest,
                Clerk-SDK staff invite, manual flush singletons in start.ts.
              </li>
              <li>
                See <code>docs/01-planning/admin-dashboard-plan-v2.md</code> §10–§12.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InboxRow({ item }: { item: InboxItem }) {
  return (
    <div className="flex items-start gap-3 rounded-md border p-3 hover:bg-muted/30">
      <div className="mt-0.5">
        {item.severity === 'critical' ? (
          <AlertTriangle aria-hidden className="h-4 w-4 text-destructive" />
        ) : (
          <AlertTriangle aria-hidden className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={severityBadgeVariant(item.severity)}>{item.severity}</Badge>
          <Badge variant="outline" className="font-mono text-[10px]">
            {item.itemKind}
          </Badge>
          <span className="text-sm font-medium">{item.title}</span>
        </div>
        <p className="text-xs text-muted-foreground">{item.body}</p>
        {item.tenantId && (
          <p className="text-[10px] text-muted-foreground">
            Tenant:{' '}
            <Link
              to={`/tenants/${item.tenantId}`}
              className="hover:text-foreground"
            >
              {item.tenantName ?? item.tenantId.slice(0, 8)}
            </Link>
          </p>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Link to={item.link}>
          <Button variant="outline" size="sm">
            <ExternalLink aria-hidden className="mr-1 h-3.5 w-3.5" />
            Open
          </Button>
        </Link>
        <SnoozeButton item={item} />
      </div>
    </div>
  );
}

function SnoozeButton({ item }: { item: InboxItem }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(4);
  const mut = useMutation({
    mutationFn: () =>
      platformApi.snoozeInbox(client, item.itemKind, item.targetId, hours),
    onSuccess: () => {
      toast.success(`Snoozed ${SNOOZE_HOURS.find((s) => s.value === hours)?.label ?? `${String(hours)}h`}`);
      void qc.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
      setOpen(false);
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Snooze failed');
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <BellOff aria-hidden className="mr-1 h-3.5 w-3.5" />
        Snooze
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Snooze inbox item</DialogTitle>
          <DialogDescription>
            Hides this item from <strong>your</strong> Inbox for the chosen
            duration. Other staff still see it. The item reappears
            automatically if it's still open after the snooze expires.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Snooze for</Label>
          <Select
            value={String(hours)}
            onChange={(e) => {
              setHours(Number(e.target.value));
            }}
          >
            {SNOOZE_HOURS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={mut.isPending}
            onClick={() => {
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Snoozing…' : 'Snooze'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <div>{value}</div>
    </div>
  );
}
